import {$, $$, createElement, empty, hide, html_chargement_en_cours} from "./Dom.ts";
import {createACompleterLabel, createAModifierLabel, initInputLabelRequis} from "./FormHelper.ts";
import {createSimpleSelect} from "./Select.ts";
import {$get} from "./Ajax.ts";
import type {Option} from "slim-select/src/slim-select/store.ts";
import {arrayIntersect, findElementUnique} from "./Utils.ts";
import SlimSelect from "slim-select";

type IValues = {
    departements: Map<string, string>
    communes: Map<string, string[]>
    communesDepart: Map<string, string>
}

type ICacheValues = {
    dep: {
        onlyInCerfa: string[],
        onlyInMap: string[],
        ok: string[],
    },
    commune: {
        onlyInCerfa: string[],
        onlyInMap: string[],
        ok: string[],
        all: string[]
    },
    n2k: {
        onlyInCerfa: string[],
        onlyInMap: string[],
        ok: string[],
    },
    rnr: {
        onlyInCerfa: string[],
        onlyInMap: string[],
        ok: string[],
    },
    pdesi: {
        onlyInCerfa: string[],
        onlyInMap: string[],
        ok: string[],
    },
}

type GeographieOptions = {
    instruction: boolean,
    interdep: boolean
}

export class GeographieForm extends EventTarget {
    private options: GeographieOptions = {
        instruction: false,
        interdep: false
    }
    private currentDepartement: string | undefined
    private values: IValues = {
        departements: new Map<string, string>(),
        communes: new Map<string, string[]>(),
        communesDepart: new Map<string, string>(),
    }
    private apiCache: ICacheValues = {
        dep: {
            onlyInCerfa: [],
            onlyInMap: [],
            ok: [],
        },
        commune: {
            onlyInCerfa: [],
            onlyInMap: [],
            ok: [],
            all: []
        },
        n2k: {
            onlyInCerfa: [],
            onlyInMap: [],
            ok: [],
        },
        rnr: {
            onlyInCerfa: [],
            onlyInMap: [],
            ok: [],
        },
        pdesi: {
            onlyInCerfa: [],
            onlyInMap: [],
            ok: [],
        }
    }
    private isInterdept: boolean = false
    private interdep_a_modifier: boolean = false

    private loading: string[] = []

    private listeCommunesParDepartements = new Map<string, string[][]>()

    dispatchLoadingEvent(action: string) {
        if (this.loading.includes(action)) {
            this.loading = this.loading.filter(value => value !== action)
        } else {
            this.loading.push(action)
        }
        if (this.loading.length === 0) {
            this.dispatchEvent(new CustomEvent("geo:loading", {detail: false}))
        } else {
            this.dispatchEvent(new CustomEvent("geo:loading", {detail: true}))
        }
    }

    async init(options?: GeographieOptions) {
        if (options) {
            this.options = {...this.options, ...options}
        }
        hide($<HTMLElement>('#div_id_ville_depart_interdep'))
        this.dispatchLoadingEvent("init")
        this.initListeners()
        await this.getCurrentSaveData()

        this.currentDepartement = $<HTMLSelectElement>("#id_departement_depart")?.slim?.getSelected()[0] ?? undefined;
        const slimVilleDepart = $<HTMLSelectElement>('#id_ville_depart')?.slim
        if (!this.currentDepartement) {
            throw new Error(`Département initial manquant impossible d'initialiser le formulaire`)
        }
        const currentDepartementLabel = $(`#id_departement_depart option[value="${this.currentDepartement}"]`)!.innerHTML
        this.listeCommunesParDepartements.set(this.currentDepartement, (slimVilleDepart?.getData() as Option[]).map((option) => ([option.value, option.text])) ?? [])
        this.values.departements.set(this.currentDepartement, currentDepartementLabel);
        const input = $<HTMLTextAreaElement>('#id_ville_depart_interdep')
        if (input) {
            if (input.classList?.contains('modifier')) {
                this.interdep_a_modifier = true;
            }
            const elements: InterdepInitialValues[] = JSON.parse(input.value)
            if (elements) {
                elements.forEach(element => {
                    this.values.communesDepart.set(element.departement_pk, element.commune_pk)
                })
            }
        }
        const departementSelect = $<HTMLSelectElement>('#id_departements_traverses')?.slim;
        if (departementSelect) {
            departementSelect.selectEl.setAttribute('data-type', "departement")
            if (!this.options.instruction && !this.options.interdep) {
                departementSelect.setSelected([...departementSelect.getSelected(), ...this.apiCache.dep.onlyInMap]);
            }
            (departementSelect.getData() as Option[]).forEach((option) => {
                if (option.selected) {
                    this.values.departements.set(option.value, option.text)
                }
            })
        }
        const selectCommunes = $<HTMLSelectElement>('#id_villes_traversees')?.slim
        this.values.communes.set(this.currentDepartement, selectCommunes?.getSelected() ?? [])
        const villeDepart = slimVilleDepart?.getSelected()[0]
        if (villeDepart) this.values.communesDepart.set(this.currentDepartement, villeDepart)

        if (this.values.departements.size > 1) {
            await this.initInterdept()
        }
        this.initDepartementTraverserListener()

        if (!this.isInterdept && selectCommunes) {
            let villes = this.apiCache.commune.all
            villes.concat(this.values.communes.get(this.currentDepartement) ?? [])
            selectCommunes.selectEl.setAttribute('data-departement', this.currentDepartement)
            // @ts-ignore
            selectCommunes?.setSelected([...selectCommunes.getSelected(), ...villes])
            selectCommunes.selectEl.addEventListener('change', this.onUpdateValues.bind(this))

            this.updateFieldStatus("commune", "id_villes_traversees")
        }

        this.updateFieldStatus("dep")
        this.updateFieldStatus("n2k")
        this.updateFieldStatus("rnr")
        this.updateFieldStatus("pdesi")
        this.dispatchLoadingEvent("init")
    }

    private initListeners() {
        $("#id_sites_natura2000")?.addEventListener('chained', () => this.initValues())
        $("#id_sites_natura2000")?.addEventListener('change', () => this.updateFieldStatus("n2k"))
        $("#id_lieux_pdesi")?.addEventListener('change', () => this.updateFieldStatus("pdesi"))
        $("#id_zones_rnr")?.addEventListener('change', () => this.updateFieldStatus("rnr"))
    }

    private async initInterdept() {
        if (!this.isInterdept) {
            const pageContainer = $<HTMLDivElement>('#div_display_interdep')
            if (pageContainer) {
                this.isInterdept = true
                hide($('#div_id_villes_traversees'))
                for (const [id, text] of this.values.departements) {
                    this.createDepartementSelect(pageContainer, id, text).catch()
                }
            }
        }
    }

    private initValues() {
        requestAnimationFrame(() => {
            const selectN2k = $<HTMLSelectElement>("#id_sites_natura2000")
            // init n2k/rnr/pdsei
            if (selectN2k?.slim) {
                selectN2k.slim.setSelected([...selectN2k.slim.getSelected(), ...this.apiCache.n2k.onlyInMap, ...this.apiCache.n2k.onlyInCerfa])
            }
            const selectPdesi = $<HTMLSelectElement>("#id_lieux_pdesi")
            if (selectPdesi?.slim) {
                selectPdesi.slim.setSelected([...selectPdesi.slim.getSelected(), ...this.apiCache.pdesi.onlyInMap, ...this.apiCache.pdesi.onlyInCerfa])
            }
            const selectRnr = $<HTMLSelectElement>("#id_zones_rnr")
            if (selectRnr?.slim) {
                selectRnr.slim.setSelected([...selectRnr.slim.getSelected(), ...this.apiCache.rnr.onlyInMap, ...this.apiCache.rnr.onlyInCerfa])
            }
        })
    }

    private initDepartementTraverserListener() {
        $('#id_departements_traverses')?.addEventListener("change", this.onDepartementChanged.bind(this))
    }

    private onDepartementChanged(event: DOMEvent<HTMLSelectElement>) {
        const slim = event.target.slim
        if (slim) {
            slim.getData().forEach((option: Option) => {
                if (option.selected) {
                    if (!$(`#departement-${option.value}`)) {
                        this.initInterdept().then(() => {
                            this.values.departements.set(option.value, option.text)
                            const pageContainer = $<HTMLDivElement>('#div_display_interdep')
                            if (pageContainer) this.createDepartementSelect(pageContainer, option.value, option.text).catch()
                        })
                    }
                } else if (this.values.departements.has(option.value)) {
                    this.removeDepartement(option.value)
                }
            })
            this.updateFieldStatus('dep')
        }
    }

    private removeDepartement(id: string) {
        $(`#departement-${id}`)?.remove()
    }

    private async createDepartementSelect(parent: HTMLElement, id: string, nom: string) {
        this.dispatchLoadingEvent(nom)
        const container = createElement('div')
        container.id = `departement-${id}`
        parent.appendChild(container)
        container.appendChild(html_chargement_en_cours(`Chargement du département ${nom}`))
        if (!this.listeCommunesParDepartements.has(id)) {
            this.listeCommunesParDepartements.set(id, await getCommunesForDepartements(id))
        }
        let elementVillesEntree
        if (this.currentDepartement !== id) {
            const villeEntreeData = {
                departement: id,
                id: `div_ville_depart_interdep_${id}`,
                name: `ville_depart_interdep_${id}`,
                label: `Commune d'entrée dans le département</br>${nom}*`,
                disabled: !(this.interdep_a_modifier && window.Openscop.liste_champ_modif.indexOf("ville_depart_interdep_" + id) >= 0),
                aModifier: (this.interdep_a_modifier && window.Openscop.liste_champ_modif.indexOf("ville_depart_interdep_" + id) >= 0),
                ACompleter: true,
            }
            elementVillesEntree = this.renderSelectVilleEntree(villeEntreeData, this.listeCommunesParDepartements.get(id) ?? [], this.values.communesDepart.get(id) ?? "")
        }
        const villesData = {
            departement: id,
            id: `div_ville_traverse_${id}`,
            name: `ville_traverse_${id}`,
            label: `Autres communes traversées dans le département</br>${nom}`,
            disabled: !(!window.Openscop.en_instruction || window.Openscop.liste_champ_modif.indexOf("villes_traversees") >= 0),
            aModifier: (window.Openscop.liste_champ_modif.indexOf("villes_traversees") >= 0),
        }
        let villes = this.apiCache.commune.all
        villes.concat(this.values.communes.get(id) ?? [])
        const elementVilles = this.renderSelectVilleAutre(villesData, this.listeCommunesParDepartements.get(id) ?? [], villes)
        empty(container)
        if (elementVillesEntree) container.appendChild(elementVillesEntree)
        container.appendChild(elementVilles)
        this.updateFieldStatus("commune", id)
        this.dispatchLoadingEvent(nom)
    }

    private renderSelectVilleEntree(inputData: any, villesDuDepartement: string[][], value: string) {
        const container = createElement('div', ['fr-grid-row', 'fr-mt-2w'])
        container.id = inputData.id

        const selectGroup = createElement('div', ['fr-select-group'])
        const label = createElement('label', ['fr-label'])
        const select = createElement('select', ['ville_depart_interdep', 'requis'])

        label.setAttribute('for', inputData.id)
        label.innerHTML = inputData.label
        select.id = inputData.name
        select.name = inputData.name
        select.setAttribute('data-type', 'ville_depart')
        select.setAttribute('data-departement', inputData.departement)

        const optionElement = createElement<'option'>('option')
        optionElement.disabled = true
        optionElement.selected = true
        optionElement.value = ""
        optionElement.innerText = 'Sélectionnez la commune de départ'
        select.appendChild(optionElement)
        if (inputData.isDisabled) {
            select.disabled = true
        }

        for (const option of villesDuDepartement) {
            const optionElement = createElement<'option'>('option')
            optionElement.value = option[0]
            optionElement.innerText = option[1]
            if (option[0] == value) {
                optionElement.selected = true
                optionElement.setAttribute('selected', 'selected')
            }
            select.appendChild(optionElement)
        }

        if (inputData.aModifier) {
            createAModifierLabel(label)
        } else if (inputData.ACompleter) {
            createACompleterLabel(label)
        }

        selectGroup.appendChild(label)
        selectGroup.appendChild(select)
        container.appendChild(selectGroup)

        select.addEventListener('change', this.onUpdateValues.bind(this))

        initInputLabelRequis(select, label)
        createSimpleSelect(select)
        return container
    }

    private renderSelectVilleAutre(inputData: any, villesDuDepartement: any[], values: string[]) {
        const container = createElement('div', ['fr-grid-row', 'fr-mt-2w'])
        container.id = inputData.id

        const selectGroup = createElement('div', ['fr-select-group'])
        const label = createElement('label', ['fr-label'])
        const select = createElement('select', ['ville_traverse_interdep', 'fr-select--openscop'])

        label.setAttribute('for', inputData.id)
        label.innerHTML = inputData.label
        select.id = inputData.name
        select.name = inputData.name
        select.multiple = true
        select.setAttribute('data-type', 'ville_traverse')
        select.setAttribute('data-departement', inputData.departement)

        if (inputData.isDisabled) select.disabled = true

        for (const ville of villesDuDepartement) {
            const optionElement = createElement<'option'>('option')
            optionElement.value = ville[0]
            optionElement.innerText = ville[1]
            if (values.includes(String(ville[0]))) {
                optionElement.selected = true
                optionElement.setAttribute('selected', 'selected')
            }
            select.appendChild(optionElement)
        }

        if (inputData.aModifier) {
            select.classList.add('modifier')
            // createAModifierLabel(label)
        }

        selectGroup.appendChild(label)
        selectGroup.appendChild(select)
        container.appendChild(selectGroup)

        select.addEventListener('change', this.onUpdateValues.bind(this))

        createSimpleSelect(select)
        return container
    }

    private onUpdateValues(event: DOMEvent<HTMLSelectElement>) {
        const select = event.target
        const type = select.getAttribute('data-type')
        const departement = select.getAttribute("data-departement")
        const slim = select.slim
        if (slim && type && departement) {
            switch (type) {
                case "ville_depart":
                    this.values.communesDepart.set(departement, slim.getSelected())
                    break
                case "ville_traverse":
                    this.values.communes.set(departement, slim.getSelected())
                    this.updateFieldStatus("commune", departement)
                    break
                case "departement":
                    this.updateFieldStatus("dep")
                    if (!this.isInterdept) {
                        this.initInterdept().catch()
                    }
                    break
            }
        }
    }

    private async getCurrentSaveData() {
        const data = await getApiCommunes("all")
        if (data) {
            Object.keys(data).forEach((key) => {
                // @ts-ignore
                const inMap = data[key][0]
                // @ts-ignore
                const inCerfa = data[key][1]
                const onlyInCerfa = findElementUnique(inCerfa, inMap)
                const onlyInMap = findElementUnique(inMap, inCerfa)
                const elementsOk = arrayIntersect(inMap, inCerfa)
                // @ts-ignore
                this.apiCache[key].onlyInCerfa = onlyInCerfa
                // @ts-ignore
                this.apiCache[key].onlyInMap = onlyInMap
                // @ts-ignore
                this.apiCache[key].ok = elementsOk
                // @ts-ignore
                this.apiCache[key].all = [].concat(onlyInMap, onlyInCerfa, elementsOk)
            })
        }
    }

    private updateFieldStatus(key: "dep" | "commune" | "n2k" | "rnr" | "pdesi", departement?: string) {
        let input: SlimSelect | undefined
        switch (key) {
            case "commune":
                if (this.isInterdept) {
                    input = $<HTMLSelectElement>(`#ville_traverse_${departement}`)?.slim
                } else {
                    input = $<HTMLSelectElement>(`#${departement}`)?.slim
                }
                break
            case "dep":
                input = $<HTMLSelectElement>(`#id_departements_traverses`)?.slim
                break
            case "n2k":
                input = $<HTMLSelectElement>(`#id_sites_natura2000`)?.slim
                break
            case "rnr":
                input = $<HTMLSelectElement>(`#id_zones_rnr`)?.slim
                break
            case "pdesi":
                input = $<HTMLSelectElement>(`#id_lieux_pdesi`)?.slim
                break
        }
        const cacheData = this.apiCache[key];
        requestAnimationFrame(() => {
            if (input) {
                (input.getData() as Option[]).forEach((option: Option) => {
                    if (option.selected) {
                        $$(`[data-id="${option.id}"]`).forEach(optionElement => {
                            optionElement?.classList.remove("data-carto-missing-carto")
                            optionElement?.classList.remove("data-carto-missing-cerfa")
                            optionElement?.classList.remove("data-carto-ok")
                            if (cacheData.onlyInCerfa.includes(option.value)) {
                                optionElement?.classList.add("data-carto-missing-carto")
                            } else if (cacheData.onlyInMap.includes(option.value)) {
                                optionElement?.classList.add("data-carto-missing-cerfa")
                            } else if (cacheData.ok.includes(option.value)) {
                                optionElement?.classList.add("data-carto-ok")
                            }
                        })
                    }
                })
            }
        })

    }
}

async function getCommunesForDepartements(departement: string) {
    let data = await $get(window.Openscop.url_liste_commune, {departement: departement})
    if (data.body) {
        if (data.response.headers.get('content-type')?.includes('application/javascript')) {
            return data.body
        } else {
            return JSON.parse(data.body)
        }
    }
}

async function getApiCommunes(type: "commune" | "n2k" | "rnr" | "dep" | "all") {
    if (window.Openscop.url_get_donnee_parcours) {
        let data = await $get(window.Openscop.url_get_donnee_parcours, {object: type})
        if (data.body) {
            return data.body as { dep: string[], commune: string[], n2k: string[], rnr: string[] }
        }
    }
    return undefined
}

// fixme : possible de supprimer ce fichier, juste faire attention aux endroit ou il est utilisé